import { useState, useEffect } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { Button } from '@comfama/comfama-ui-react';
import './libranzasAccionesPago.scss';

// Gatsby solo permite una useStaticQuery por archivo — se combinan con aliases
const ACCIONES_PAGO_QUERY = graphql`
    query LibranzasAccionesPagoContent {
        placetopay: allContentfulTemplate(
            filter: { titulo: { eq: "Elige el medio de pago que prefieras > Placetopay" } }
        ) {
            nodes {
                nombre
                subtitulo
                imagen {
                    url
                    contentful_id
                }
            }
        }
        tarjeta: allContentfulTemplate(
            filter: { titulo: { eq: "Elige el medio de pago que prefieras > Tarjeta de crédito" } }
        ) {
            nodes {
                nombre
                subtitulo
                imagen {
                    url
                    contentful_id
                }
            }
        }
    }
`;

interface MetodoPagoContent {
    nombre: string;
    subtitulo: string;
    imagenUrl: string | null;
}

function adaptMetodoPago(
    alias: any,
    fallbackNombre: string,
    fallbackSubtitulo: string
): MetodoPagoContent {
    const node = alias?.nodes?.[0];
    return {
        nombre: node?.nombre ?? fallbackNombre,
        subtitulo: node?.subtitulo ?? fallbackSubtitulo,
        imagenUrl: node?.imagen?.url ?? node?.imagen?.[0]?.url ?? null,
    };
}

export interface PaymentStep {
    nombre?: string;
    subtitulo?: string;
    imagen?: Array<{
        title?: string;
        file?: { url?: string };
    }>;
}

export interface PaymentHeadInfo {
    nombre?: string;
    subtitulo?: string;
    pasos?: PaymentStep[];
}

interface LibranzasAccionesPagoProps {
    termsAccepted: boolean;
    onTermsToggle: () => void;
    onBack: () => void;
    onCancel?: () => void;
    onGoToConfirmacion: () => void;
    pagarDisabled: boolean;
    paymentHeadInfo?: PaymentHeadInfo;
    paymentMethodRefs?: PaymentStep[];
}

const resolveImgUrl = (url?: string) =>
    url ? (url.startsWith('//') ? `https:${url}` : url) : null;

const LibranzasAccionesPago = ({
    termsAccepted,
    onTermsToggle,
    onBack,
    onCancel,
    onGoToConfirmacion,
    pagarDisabled,
    paymentHeadInfo,
    paymentMethodRefs,
}: LibranzasAccionesPagoProps) => {
    const cmsData = useStaticQuery(ACCIONES_PAGO_QUERY);
    const placeToPayContent = adaptMetodoPago(cmsData?.placetopay, 'PlaceToPay', 'Cuentas débito ahorro y corriente');
    const tarjetaContent    = adaptMetodoPago(cmsData?.tarjeta,    'Tarjeta de crédito', 'Visa, MasterCard y American Express');

    const [isDark, setIsDark] = useState(false);

    useEffect(() => {
        setIsDark(document?.body?.classList?.contains('dark-mode') ?? false);
        const observer = new MutationObserver(() => {
            setIsDark(document.body.classList.contains('dark-mode'));
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    return (
        <div className="m-libranzas-acciones-pago">
            <div className="m-libranzas-acciones-pago__heading">
                <h2 className="m-libranzas-acciones-pago__heading__title">Pago en línea</h2>
                <p className="m-libranzas-acciones-pago__heading__subtitle">
                    Pagos en línea por PSE y tarjeta de crédito
                </p>
            </div>
            {paymentHeadInfo && (
                <div className="m-payment__head">
                    <div className="m-payment__head__info">
                        {(paymentHeadInfo.nombre || paymentHeadInfo.subtitulo) && (
                            <div className="m-payment__head__info__title">
                                {paymentHeadInfo.nombre && (
                                    <p className="m-payment__head__info__title__pay">
                                        {paymentHeadInfo.nombre}
                                    </p>
                                )}
                                {paymentHeadInfo.subtitulo && (
                                    <p className="m-payment__head__info__title__desc">
                                        {paymentHeadInfo.subtitulo}
                                    </p>
                                )}
                            </div>
                        )}
                        {paymentHeadInfo.pasos && paymentHeadInfo.pasos.length > 0 && (
                            <div className="m-payment__head__info__content">
                                {paymentHeadInfo.pasos.map((paso, idx) => {
                                    const imgEntry = paso.imagen?.[isDark ? 1 : 0] ?? paso.imagen?.[0];
                                    const imgUrl = resolveImgUrl(imgEntry?.file?.url);
                                    return (
                                        <div key={idx} className="m-payment__head__info__content__item">
                                            <div className="m-payment__head__info__content__item__description">
                                                <div className="m-payment__head__info__content__item__description__number">
                                                    <p>{idx + 1}</p>
                                                </div>
                                                <div className="m-payment__head__info__content__item__description__content">
                                                    {paso.nombre && (
                                                        <p className="m-payment__head__info__content__item__description__content__name">
                                                            {paso.nombre}
                                                        </p>
                                                    )}
                                                    {paso.subtitulo && (
                                                        <p className="m-payment__head__info__content__item__description__content__desc">
                                                            {paso.subtitulo}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            {imgUrl && (
                                                <div className="m-payment__head__info__content__item__img">
                                                    <img
                                                        src={imgUrl}
                                                        alt={imgEntry?.title ?? ''}
                                                        title={imgEntry?.title ?? ''}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="m-payment__body">
                <div className="m-payment__body__methods">
                    <div className="m-payment__head__info__content__item">
                        <div className="m-payment__head__info__content__item__description">
                            <div className="m-payment__head__info__content__item__description__number">
                                <p>1</p>
                            </div>
                            <div className="m-payment__head__info__content__item__description__content">
                                <p className="m-payment__head__info__content__item__description__content__name">
                                    {placeToPayContent.nombre}
                                </p>
                                <p className="m-payment__head__info__content__item__description__content__desc">
                                    {placeToPayContent.subtitulo}
                                </p>
                            </div>
                        </div>
                        {placeToPayContent.imagenUrl && (
                            <div className="m-payment__head__info__content__item__img">
                                <img
                                    src={placeToPayContent.imagenUrl}
                                    alt={placeToPayContent.nombre}
                                    title={placeToPayContent.nombre}
                                />
                            </div>
                        )}
                    </div>
                    <div className="m-payment__head__info__content__item">
                        <div className="m-payment__head__info__content__item__description">
                            <div className="m-payment__head__info__content__item__description__number">
                                <p>2</p>
                            </div>
                            <div className="m-payment__head__info__content__item__description__content">
                                <p className="m-payment__head__info__content__item__description__content__name">
                                    {tarjetaContent.nombre}
                                </p>
                                <p className="m-payment__head__info__content__item__description__content__desc">
                                    {tarjetaContent.subtitulo}
                                </p>
                            </div>
                        </div>
                        {tarjetaContent.imagenUrl && (
                            <div className="m-payment__head__info__content__item__img">
                                <img
                                    src={tarjetaContent.imagenUrl}
                                    alt={tarjetaContent.nombre}
                                    title={tarjetaContent.nombre}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="m-payment__body__term">
                    <div
                        className={`m-payment__body__term__check${termsAccepted ? '--active' : ''}`}
                        onClick={onTermsToggle}
                        data-testid="terms-libranzas"
                        role="checkbox"
                        aria-checked={termsAccepted}
                    >
                        {termsAccepted && <i className="icon-check1" />}
                    </div>
                    <span className="m-payment__body__term__desc" onClick={onTermsToggle}>
                        Acepto los{' '}
                        <a
                            href="https://www.comfama.com/ayuda/terminos-y-condiciones/generales/terminos-y-condiciones-web-comfama/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="m-libranzas-acciones-pago__terms-link"
                            onClick={(e) => e.stopPropagation()}
                        >
                            términos y condiciones de pago
                        </a>
                    </span>
                </div>
                {paymentMethodRefs && paymentMethodRefs.length > 0 && (
                    <div className="m-libranzas-acciones-pago__payment-methods">
                        {paymentMethodRefs.map((ref, idx) =>
                            ref.nombre ? (
                                <span
                                    key={idx}
                                    className="m-libranzas-acciones-pago__payment-method-tag"
                                >
                                    {ref.nombre}
                                </span>
                            ) : null
                        )}
                    </div>
                )}
                <div className="m-libranzas-acciones-pago__buttons">
                    <Button variant="outlined" color="primary" onClick={onBack}>
                        Atrás
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onGoToConfirmacion}
                        disabled={pagarDisabled}
                    >
                        Pagar
                    </Button>
                </div>
                {onCancel && (
                    <button type="button" onClick={onCancel}>
                        Cancelar
                    </button>
                )}
            </div>
        </div>
    );
};

export default LibranzasAccionesPago;
